<template>
    <!-- KPI-04B - Dettaglio delle quattro KPI economiche.
         READ-ONLY: nessuna chiamata API, nessuna scrittura, nessuna formula
         ricalcolata. Le righe sono quelle gia' autorizzate nel payload della
         Dashboard; qui vengono solo filtrate, ordinate e formattate. -->
    <div v-if="aperto" class="modal fade show kd-modal" tabindex="-1" role="dialog"
         aria-modal="true" :aria-labelledby="titoloId"
         style="display: block; background: rgba(0, 0, 0, 0.5);"
         @click.self="chiudi">
        <div class="modal-dialog modal-xl modal-fullscreen-sm-down" role="document">
            <div class="modal-content">

                <div class="modal-header kd-header" :class="classeTema">
                    <div class="kd-header-riga">
                        <h5 class="modal-title kd-titolo mb-0" :id="titoloId">{{ titolo }}</h5>
                        <span v-if="sottotitolo" class="kd-pill">{{ sottotitolo }}</span>
                        <span v-if="periodoLabel" class="kd-periodo">{{ periodoLabel }}</span>
                    </div>
                    <button type="button" class="btn-close btn-close-white" ref="btnChiudi"
                            @click="chiudi" aria-label="Chiudi"></button>
                </div>

                <div class="modal-body kd-body">

                    <div v-if="!disponibile" class="alert alert-warning mb-0" role="alert">
                        Dati economici non disponibili per il periodo e i filtri correnti.
                    </div>

                    <div v-else-if="nonApplicabile" class="alert alert-info mb-0" role="alert">
                        KPI non applicabile: il filtro Stato seleziona esclusivamente
                        PREVENTIVO/BOZZA. Le KPI operative non producono un valore.
                    </div>

                    <template v-else>
                        <!-- Riepilogo: tutti gli importi vengono dal backend. -->
                        <div class="kd-riepilogo">
                            <div v-for="t in tessere" :key="'kd-t-' + t.label" class="kd-tessera">
                                <div class="kd-tessera-label">{{ t.label }}</div>
                                <div class="kd-tessera-valore" :class="{ 'kd-negativo': t.negativo }">
                                    {{ t.valore }}
                                </div>
                            </div>
                        </div>

                        <div v-if="!righe.length" class="text-muted py-4 text-center">
                            Nessun progetto nel periodo e con i filtri correnti.
                        </div>

                        <div v-else class="table-responsive kd-tabella-wrap">
                            <table class="table table-sm table-hover kd-tabella mb-0">
                                <thead>
                                    <tr>
                                        <th class="kd-col-progetto">Progetto</th>
                                        <th v-if="tipo === 'previsionale'">Tipo</th>
                                        <th>Stato</th>
                                        <th>Data evento</th>
                                        <th v-for="c in colonne" :key="'kd-c-' + c.key"
                                            class="text-end">{{ c.label }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="r in righe" :key="'kd-r-' + r.project_id"
                                        :class="{ 'kd-riga-preventivo': tipo === 'previsionale' && r.preventivo,
                                                  'kd-riga-negativa': isNegativa(r) }">
                                        <td class="kd-col-progetto">
                                            <span class="kd-codice">{{ r.code || ('#' + r.project_id) }}</span>
                                            <span class="kd-titolo-progetto">{{ r.title || '—' }}</span>
                                        </td>
                                        <td v-if="tipo === 'previsionale'">
                                            <span class="badge" :class="r.preventivo ? 'bg-secondary' : 'bg-success'">
                                                {{ r.preventivo ? 'Preventivo' : 'Operativo' }}
                                            </span>
                                        </td>
                                        <td>{{ r.state || '—' }}</td>
                                        <td class="text-nowrap">{{ dataEtichetta(r.date_event) }}</td>
                                        <td v-for="c in colonne" :key="'kd-v-' + c.key"
                                            class="text-end text-nowrap"
                                            :class="{ 'kd-negativo': isNegativo(c.field, r) }">
                                            {{ formatta(c, r) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-if="tipo === 'ricavi'" class="kd-nota">
                            Il dettaglio cliente non e' incluso nel payload economico della Dashboard.
                        </div>

                        <!-- Controllo di riconciliazione: somma delle righe mostrate
                             contro l'aggregato restituito dal backend. -->
                        <div class="kd-riconciliazione" :class="riconciliaOk ? 'kd-ok' : 'kd-ko'">
                            <template v-if="riconciliaOk">
                                Riconciliazione: somma righe
                                <strong>{{ formattaImporto(sommaRighe) }}</strong>
                                = totale card <strong>{{ formattaImporto(totaleAtteso) }}</strong>
                            </template>
                            <template v-else>
                                Attenzione: somma righe <strong>{{ formattaImporto(sommaRighe) }}</strong>
                                diversa dal totale card <strong>{{ formattaImporto(totaleAtteso) }}</strong>
                                ({{ formattaImporto(sommaRighe - totaleAtteso) }}).
                            </template>
                        </div>
                    </template>

                </div>

                <div class="modal-footer kd-footer">
                    <span class="kd-conteggio">{{ conteggioLabel }}<template v-if="filtriAttivi"> · {{ filtriAttivi }} filtri attivi</template></span>
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="chiudi">
                        Chiudi
                    </button>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
const FORMAT_EUR = new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const FORMAT_PERCENT = new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const TITOLI = {
    ricavi: "Ricavi operativi",
    costi: "Costi operativi",
    margine: "Margine operativo",
    previsionale: "Previsionale",
};

const SOTTOTITOLI = {
    ricavi: "Progetti operativi",
    costi: "Acquisti e commissioni separate",
    margine: "Euro e percentuale aggregata",
    previsionale: "Operativi + preventivi",
};

const TEMA = {
    ricavi: "kd-header--blue",
    costi: "kd-header--red",
    margine: "kd-header--green",
    previsionale: "kd-header--purple",
};

/* Colonne delle tabelle. `field` = percorso nel payload; `amount` = importo
   usato per la riconciliazione (null = colonna descrittiva). */
const COLONNE = {
    ricavi: [
        { key: "budget_parziale", label: "Vendita budget", field: "budget.parziale", amount: false },
        { key: "budget_fee", label: "Fee", field: "budget.fee", amount: false },
        { key: "sconto", label: "Sconti", field: "sconto", amount: false },
        { key: "cons_parziale", label: "Vendita consuntivo", field: "consuntivo.parziale", amount: false },
        { key: "cons_fee", label: "Fee consuntivo", field: "consuntivo.fee", amount: false },
        { key: "storni", label: "Storni", field: "storni", amount: false },
        { key: "totale_finale", label: "Totale finale", field: "totale_finale", amount: true },
    ],
    costi: [
        { key: "acquisto", label: "Acquisto", field: "acquisto", amount: true },
        { key: "note_spese", label: "Note spese", field: "note_spese", amount: false },
        { key: "margine", label: "Margine", field: "margine", amount: false },
    ],
    margine: [
        { key: "ricavi", label: "Ricavi", field: "ricavi", amount: false },
        { key: "acquisto", label: "Acquisti", field: "acquisto", amount: false },
        { key: "commissioni", label: "Commissioni", field: "commissioni", amount: false },
        { key: "margine", label: "Margine", field: "margine", amount: true },
        { key: "margine_percent", label: "Margine %", field: "margine_percent", amount: false, percent: true },
    ],
    previsionale: [
        { key: "ricavi", label: "Ricavi", field: "ricavi", amount: true },
        { key: "acquisto", label: "Acquisti", field: "acquisto", amount: false },
        { key: "commissioni", label: "Commissioni", field: "commissioni", amount: false },
        { key: "margine", label: "Margine", field: "margine", amount: false },
    ],
};

export default {
    name: "KpiDetailModal",

    props: {
        aperto: { type: Boolean, default: false },
        /* 'ricavi' | 'costi' | 'margine' | 'previsionale' */
        tipo: { type: String, default: "" },
        /* Blocco data.economia del payload Dashboard. */
        economia: { type: Object, default: null },
        /* Contratto della Dashboard (stessa forma di data.periodo). */
        periodo: { type: Object, default: null },
        /* Filtri applicati: usati solo per la descrizione, mai per ricalcolare. */
        filtri: { type: Object, default: null },
    },

    emits: ["chiudi"],

    data() {
        return {
            titoloId: "kd-titolo-" + Math.random().toString(36).slice(2, 9),
            focusPrecedente: null,
        };
    },

    computed: {
        disponibile() {
            return !!(this.economia && this.economia.ok === true);
        },
        titolo() {
            return TITOLI[this.tipo] || "Dettaglio economico";
        },
        sottotitolo() {
            return SOTTOTITOLI[this.tipo] || "";
        },
        classeTema() {
            return TEMA[this.tipo] || "kd-header--blue";
        },
        operativo() {
            return this.disponibile ? this.economia.operativo : null;
        },
        previsionale() {
            return this.disponibile ? this.economia.previsionale : null;
        },
        nonApplicabile() {
            if (this.tipo === "previsionale") return false;
            return !!(this.operativo && this.operativo.applicabile === false);
        },
        righe() {
            if (!this.disponibile || this.nonApplicabile) return [];
            const tutte = Array.isArray(this.economia.progetti) ? this.economia.progetti : [];
            const ammesse = (this.tipo === "previsionale")
                ? tutte.slice()
                : tutte.filter((r) => r && r.incluso_operativo === true);
            /* Ordinamento SOLO presentazionale: data evento, poi codice. */
            return ammesse.sort((a, b) => {
                const da = String(a.date_event || "");
                const db = String(b.date_event || "");
                if (da !== db) return da < db ? -1 : 1;
                return String(a.code || "").localeCompare(String(b.code || ""));
            });
        },
        colonne() {
            return COLONNE[this.tipo] || [];
        },
        totaleAtteso() {
            if (!this.disponibile || this.nonApplicabile) return 0;
            if (this.tipo === "ricavi") return this.num(this.operativo && this.operativo.ricavi);
            if (this.tipo === "costi") return this.num(this.operativo && this.operativo.acquisto);
            if (this.tipo === "margine") return this.num(this.operativo && this.operativo.margine);
            if (this.tipo === "previsionale") return this.num(this.previsionale && this.previsionale.ricavi);
            return 0;
        },
        sommaRighe() {
            const campo = this.colonne.find((c) => c.amount === true);
            if (!campo) return 0;
            return this.righe.reduce((acc, r) => acc + this.num(this.leggi(r, campo.field)), 0);
        },
        riconciliaOk() {
            return Math.abs(this.sommaRighe - this.totaleAtteso) < 0.005;
        },
        periodoLabel() {
            const p = this.periodo;
            if (!p || !p.date_from || !p.date_to) return "";
            return this.dataEtichetta(p.date_from) + " → " + this.dataEtichetta(p.date_to);
        },
        conteggioLabel() {
            if (!this.disponibile) return "";
            if (this.nonApplicabile) return "Nessuna riga operativa";
            const n = this.righe.length;
            return n + (n === 1 ? " progetto" : " progetti") + " nel dettaglio";
        },
        filtriAttivi() {
            const f = this.filtri || {};
            return Object.keys(f).filter((k) => {
                const v = f[k];
                return v !== null && v !== undefined && v !== "";
            }).length;
        },
        tessere() {
            if (!this.disponibile) return [];
            if (this.tipo === "ricavi") {
                return [
                    { label: "Totale ricavi", valore: this.formattaImporto(this.operativo && this.operativo.ricavi) },
                    { label: "Progetti operativi", valore: this.num(this.operativo && this.operativo.progetti) },
                ];
            }
            if (this.tipo === "costi") {
                return [
                    { label: "Totale acquisti", valore: this.formattaImporto(this.operativo && this.operativo.acquisto) },
                    { label: "Commissioni (separate)", valore: this.formattaImporto(this.operativo && this.operativo.commissioni) },
                    { label: "Progetti operativi", valore: this.num(this.operativo && this.operativo.progetti) },
                ];
            }
            if (this.tipo === "margine") {
                const pct = (this.operativo && this.operativo.percentuale_disponibile === true)
                    ? this.formattaPercento(this.operativo.margine_percent) : "n.d.";
                return [
                    { label: "Margine", valore: this.formattaImporto(this.operativo && this.operativo.margine),
                      negativo: this.num(this.operativo && this.operativo.margine) < 0 },
                    { label: "Margine % (aggregato)", valore: pct },
                    { label: "Ricavi", valore: this.formattaImporto(this.operativo && this.operativo.ricavi) },
                    { label: "Acquisti", valore: this.formattaImporto(this.operativo && this.operativo.acquisto) },
                    { label: "Commissioni", valore: this.formattaImporto(this.operativo && this.operativo.commissioni) },
                ];
            }
            if (this.tipo === "previsionale") {
                return [
                    { label: "Ricavi previsionali", valore: this.formattaImporto(this.previsionale && this.previsionale.ricavi) },
                    { label: "Ricavi operativi", valore: this.formattaImporto(this.previsionale && this.previsionale.ricavi_operativi) },
                    { label: "Ricavi preventivi", valore: this.formattaImporto(this.previsionale && this.previsionale.ricavi_preventivi) },
                    { label: "Progetti totali", valore: this.num(this.previsionale && this.previsionale.progetti_totali) },
                    { label: "Preventivi", valore: this.num(this.previsionale && this.previsionale.progetti_preventivi) },
                ];
            }
            return [];
        },
    },

    watch: {
        aperto(val) {
            if (val) {
                this.focusPrecedente = document.activeElement;
                window.addEventListener("keydown", this.onKeydown);
                this.$nextTick(() => {
                    if (this.$refs.btnChiudi) this.$refs.btnChiudi.focus();
                });
            } else {
                this.rilascia();
            }
        },
    },

    beforeUnmount() {
        this.rilascia();
    },

    methods: {
        num(v) {
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        },
        leggi(obj, path) {
            if (!obj || !path) return null;
            return path.split(".").reduce((acc, k) => (acc == null ? null : acc[k]), obj);
        },
        formattaImporto(v) {
            return FORMAT_EUR.format(this.num(v));
        },
        formattaPercento(v) {
            return FORMAT_PERCENT.format(this.num(v)) + "%";
        },
        formatta(col, riga) {
            const v = this.leggi(riga, col.field);
            if (col.percent) {
                if (v === null || v === undefined) return "n.d.";
                return this.formattaPercento(v);
            }
            return FORMAT_EUR.format(this.num(v));
        },
        isNegativo(path, riga) {
            return this.num(this.leggi(riga, path)) < 0;
        },
        isNegativa(riga) {
            if (this.tipo === "margine") return this.num(riga.margine) < 0;
            if (this.tipo === "costi") return this.num(riga.margine) < 0;
            return false;
        },
        dataEtichetta(iso) {
            if (!iso) return "—";
            const s = String(iso);
            if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
            return s.slice(8, 10) + "/" + s.slice(5, 7) + "/" + s.slice(0, 4);
        },
        onKeydown(e) {
            if (e && e.key === "Escape") {
                e.preventDefault();
                this.chiudi();
            }
        },
        chiudi() {
            this.$emit("chiudi");
        },
        rilascia() {
            window.removeEventListener("keydown", this.onKeydown);
            if (this.focusPrecedente && typeof this.focusPrecedente.focus === "function") {
                this.focusPrecedente.focus();
            }
            this.focusPrecedente = null;
        },
    },
}
</script>

<style scoped>
.kd-modal .modal-content {
    border: 0;
    border-radius: 14px;
    overflow: hidden;
    max-height: calc(100vh - 2rem);
}
.kd-header {
    color: #fff;
    border: 0;
    padding: .85rem 1.1rem;
}
.kd-header--blue { background: #2563eb; }
.kd-header--red { background: #dc2626; }
.kd-header--green { background: #059669; }
.kd-header--purple { background: #7c3aed; }
.kd-header-riga {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .5rem .75rem;
    min-width: 0;
}
.kd-titolo {
    font-size: 1.05rem;
    font-weight: 600;
}
.kd-pill {
    background: rgba(255, 255, 255, .18);
    border-radius: 999px;
    padding: .15rem .6rem;
    font-size: .75rem;
}
.kd-periodo {
    font-size: .78rem;
    opacity: .9;
}
.kd-body {
    overflow-y: auto;
}
.kd-riepilogo {
    display: flex;
    flex-wrap: wrap;
    gap: .6rem;
    margin-bottom: 1rem;
}
.kd-tessera {
    flex: 1 1 150px;
    border: 1px solid #e9edf2;
    border-radius: 10px;
    padding: .55rem .7rem;
    background: #f8fafc;
}
.kd-tessera-label {
    color: #64748b;
    font-size: .72rem;
    text-transform: uppercase;
    letter-spacing: .02em;
}
.kd-tessera-valore {
    font-weight: 700;
    font-size: 1rem;
    color: #0f172a;
}
.kd-tabella-wrap {
    border: 1px solid #e9edf2;
    border-radius: 10px;
    max-height: 52vh;
}
.kd-tabella {
    font-size: .82rem;
}
.kd-tabella thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f1f5f9;
    font-weight: 600;
    color: #475569;
    white-space: nowrap;
}
.kd-col-progetto {
    min-width: 210px;
}
.kd-codice {
    display: block;
    font-weight: 600;
    color: #0f172a;
}
.kd-titolo-progetto {
    display: block;
    color: #64748b;
    font-size: .76rem;
}
.kd-riga-preventivo td {
    background: #fef9c3;
}
.kd-riga-negativa td {
    background: #fef2f2;
}
.kd-negativo {
    color: #b91c1c;
    font-weight: 600;
}
.kd-nota {
    margin-top: .5rem;
    color: #64748b;
    font-size: .75rem;
}
.kd-riconciliazione {
    margin-top: .85rem;
    padding: .5rem .7rem;
    border-radius: 8px;
    font-size: .8rem;
}
.kd-riconciliazione.kd-ok {
    background: #ecfdf5;
    color: #065f46;
}
.kd-riconciliazione.kd-ko {
    background: #fef2f2;
    color: #991b1b;
}
.kd-footer {
    justify-content: space-between;
    align-items: center;
}
.kd-conteggio {
    color: #64748b;
    font-size: .78rem;
}
@media (max-width: 575.98px) {
    .kd-riepilogo {
        gap: .45rem;
    }
    .kd-tessera {
        flex: 1 1 44%;
    }
    .kd-tabella {
        font-size: .78rem;
    }
}
</style>
